import { Button } from "@mui/material";
import React, { createContext, useContext, useState } from "react";

/**
 * ConfirmFn
 * - titleMessage?: 커스텀 컨펌 제목 (default: Confirm)
 * - message?: 커스텀 컴펌 내용
 * - return: Promise<boolean> (Y: true N: false)
 */
type ConfirmFn = (titleMessage?: string, message?: string) => Promise<boolean>;
interface ConfirmContextType {
  customConfirm: ConfirmFn;
}

/**
 * ConfirmContext
 * - 기본값으로 항상 reject하는 confirm 함수를 세팅하여, Provider 미적용 시 사용을 방지함
 */
const ConfirmContext = createContext<ConfirmContextType>({
  customConfirm: () => Promise.reject(false),
});

/**
 * useConfirm
 * - ConfirmContext에서 confirm 함수를 꺼내쓰는 hook
 */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("confirmProvider가 루트에 없습니다."); // App.js에 ConfirmContainer로 감싸고 있어 오류 생길일 없음
  return ctx.customConfirm;
};

/**
 * ConfirmState
 * - titleMessage?: 커스텀 컨펌 제목 (default: Confirm)
 * - message: 커스텀 컨펌 내용
 * - resolve: 커스텀 컨펌 Y, N
 */
type ConfirmState = {
  titleMessage?: string;
  message: string;
  resolve: (_: boolean) => void;
};

/**
 * ConfirmContainer
 * - useConfirm confirm함수를 꺼내면 전역으로 사용가능
 * - 사용자의 응답을 Promise로 반환
 * @param param0
 * @returns
 */
const ConfirmContainer = ({ children }: { children: React.ReactNode }) => {
  // 컨펌 상태
  const [state, setState] = useState<ConfirmState | null>(null);

  /**
   * confirm
   * - 호출 시 커스텀 컨펌 열림 (확안, 취소: Promise<boolean>)
   * - 첫 번째 인자 titleOrMsg, 두 번째 인자 maybeMsg 유무에 따라 제목/본문 분기
   * @param titleOrMsg
   * @param maybeMsg
   * @returns
   */
  const customConfirm: ConfirmFn = (titleOrMsg, maybeMsg) =>
    new Promise<boolean>((resolve) => {
      const isTowArgs = maybeMsg != undefined;
      const titleMessage = isTowArgs ? titleOrMsg! : "Confirm";
      const message = isTowArgs ? maybeMsg! : titleOrMsg ?? "";

      setState({ titleMessage, message, resolve });
    });

  /**
   * handle
   * - 커스텀 컨펌 버튼(확인, 취소) 클릭 시 호출
   * - resolve 호출 후 state 초기화하여 컨펌 닫기
   * @param _
   */
  const handle = (_: boolean) => {
    state?.resolve(_);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ customConfirm }}>
      {children}
      {state && (
        <div
          className="dialog-container"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: "9997",
          }}
        >
          {/* 배경 */}
          <div
            onClick={() => handle(false)}
            style={{
              position: "relative",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.3)",
              zIndex: "9998",
            }}
          ></div>
          {/* 입력 창 */}
          <div
            className="dialog"
            style={{
              background: "#fff",
              borderRadius: "4px",
              minWidth: "500px",
              maxWidth: "max-content",
              boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: "9999",
            }}
          >
            {/* 제목목 */}
            <h2
              style={{
                textAlign: "left",
                margin: "0 0 16px 0",
                padding: "32px 32px 0 32px",
              }}
            >
              {state.titleMessage}
            </h2>
            {/* 내용용 */}
            <div
              style={{
                padding: "16px 32px",
                whiteSpace: "pre-line",
                lineHeight: "1,5rem",
                textAlign: "left",
              }}
            >
              {state.message}
            </div>
            {/* 버튼들 */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: "16px",
                padding: "0 16px 16px 16px",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => handle(true)}
                sx={{
                  ":hover": {
                    color: "#ffffff",
                    backgroundColor: "#236daa",
                  },
                }}
              >
                확인
              </Button>
              <Button
                variant="outlined"
                onClick={() => handle(false)}
                sx={{
                  ":hover": {
                    color: "#ffffff",
                    backgroundColor: "#236daa",
                  },
                }}
              >
                취소
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmContainer;
